import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import BZChart from "./BZChart";

const BaziHistory = () => {
  const navigate = useNavigate();
  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchCharts();
  }, [page]);

  const fetchCharts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`/api/bazi/list?page=${page}&per_page=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setCharts(response.data.data);
        setTotalPages(response.data.pages);
      } else {
        // Fallback for other formats
        let data = response.data;
        if (data.charts) {
             setCharts(data.charts);
             setTotalPages(data.pages || 1);
        } else if (Array.isArray(data)) {
             setCharts(data);
             setTotalPages(1);
        } else if (data.data && Array.isArray(data.data)) {
             setCharts(data.data);
             setTotalPages(data.pages || 1);
        } else {
             setError(response.data.error || '获取数据失败');
        }
      }
    } catch (err) {
      console.error(err);
      setError('获取历史记录失败');
      if (err.response && err.response.status === 401) {
          navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (id) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/bazi/charts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      const chartData = data.data || data;
      
      if (chartData) {
         const fullChart = BZChart.getFullBZChart(chartData);
         navigate('/bazi/baziDetail', { state: { result: fullChart } });
      } else {
         alert('获取详情失败');
      }

    } catch (err) {
      console.error(err);
      alert('获取详情失败');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这条记录吗？')) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/bazi/charts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCharts(prev => prev.filter(c => (c.id || c._id || c.chart_id) !== id));
      
    } catch (err) {
      console.error(err);
      alert('删除失败');
    }
  };

  // Client-side pagination removed, using server-side pagination
  // const totalPages = Math.max(1, Math.ceil(charts.length / PAGE_SIZE));
  // const pagedCharts = charts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="返回上一页"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h2 className="text-2xl font-bold text-gray-900">我的八字排盘</h2>
          </div>
          <button
            onClick={() => navigate('/bazi/index')}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            新建排盘
          </button>
        </div>

        {loading ? (
          <div className="text-center py-10">加载中...</div>
        ) : error ? (
          <div className="text-center py-10 text-red-600">{error}</div>
        ) : charts.length === 0 ? (
          <div className="text-center py-10 text-gray-500">暂无记录</div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {charts.map((chart) => (
                <li key={chart.id || chart._id || chart.chart_id}>
                  <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium text-indigo-600 truncate">
                            {chart.mingzhu_name || '未命名'} 
                            <span className="text-gray-500 ml-2 text-xs">
                                {chart.mingzhu_gender === 'male' ? '男' : '女'}
                            </span>
                            </p>
                        </div>
                        
                        <div className="mt-2 flex justify-between">
                          <div className="sm:flex flex-col">
                            <p className="flex items-center text-sm text-gray-500">
                              出生日期：{chart.birth_info}
                            </p>
                            <p className="mt-1 flex items-center text-sm text-gray-500">
                              八字：{chart.year_pillar} {chart.month_pillar} {chart.day_pillar} {chart.hour_pillar}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="ml-5 flex-shrink-0 flex space-x-2">
                        <button
                          onClick={() => handleView(chart.id || chart._id || chart.chart_id)}
                          className="px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                        >
                          查看
                        </button>
                        <button
                          onClick={() => handleDelete(chart.id || chart._id || chart.chart_id)}
                          className="px-3 py-1 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
                        >
                          删除
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            
            {/* Pagination */}
            {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                            上一页
                        </button>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                            下一页
                        </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                第 <span className="font-medium">{page}</span> 页，共 <span className="font-medium">{totalPages}</span> 页
                            </p>
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    上一页
                                </button>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    下一页
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BaziHistory;

